'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { dispatch, useSelector } from '@/lib/store'
import { updateCommonReducerData } from '@/lib/reducers/commonReducer'
import { updateLoginData } from '@/lib/reducers/loginReducer'
import {
  BLOG_PAGE,
  CURRENT_SUBSIDIARY,
  FORM_TYPE_LAB_TEST,
  MY_REPORTS_PAGE,
  NEARBY_LABS_PAGE,
  SUBSIDIARY_CONFIG,
} from '@/utils/constants'
import { loadState } from '@/utils/localStorage'
import { eventConstants, trackEvent } from '@/utils/events'
import { cn } from '@/lib/utils'
import {
  getCarePackageCategoryUrl,
  getCarePackageNonCityCategoryUrl,
  getDiseaseCategoriesProductListUrl,
  getDiseaseCategoriesUrl,
  getNonCityProductUrl,
  getPopularHealthCheckupUrl,
  getProductUrl,
  isNonCityRoute,
} from '@/utils/commonFunc'
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import NavSubmenu from './NavSubmenu'
import usePopularHealthCheckupsAndSearchProducts from '@/hooks/usePopularHealthCheckupsAndSearchProducts'
import type { SharedCatalog } from '@/helper/serverFetchers/sharedCatalog'
import { useMediaQuery } from '@/hooks/use-media-query'
import PlayStoreIcon from '@/assets/icons/PlayStoreIcon'
import AppStoreIcon from '@/assets/icons/AppStoreIcon'

const loggedInMobileNumber = loadState('ctxVal') || null

const SecondaryNav = ({
  sharedCatalog,
}: {
  sharedCatalog?: SharedCatalog | null
}) => {
  const packageState = useSelector((state) => state.package) || {}
  const commonState = useSelector((state) => state.common) || {}
  const loginState = useSelector((state) => state.login) || {}
  const cityState = useSelector((state) => state.city) || {}

  const isNonCity = isNonCityRoute(usePathname() || '')
  // Undefined on non-city routes so the selected city cannot leak back into these links; /health-checkup has no non-city route, so its "View All" keeps a city below.
  const linkCitySlug = isNonCity
    ? undefined
    : cityState?.currentCityObj?.slugName

  // Only the lg menu shows this data, so mobile visitors never trigger the fetch.
  const { value: isDesktop } = useMediaQuery('(min-width: 1024px)')
  usePopularHealthCheckupsAndSearchProducts({ loadPopular: isDesktop })

  const myReportClickHandler = (e: any) => {
    // Only block the anchor when logged out; otherwise let it navigate like any link.
    if (!loginState?.isUserLoggedIn) {
      e.preventDefault()
      dispatch(updateLoginData({ ...loginState, loginModalIsOpen: true }))
      return
    }
    trackEvent(eventConstants.MY_REPORTS, {
      u_mb: loggedInMobileNumber,
      subsidiary: CURRENT_SUBSIDIARY,
    })
  }

  return (
    <div className="container hidden lg:block max-w-full pt-[70px] border-b border-solid border-gray-200 bg-white-100 relative z-20">
      <div className="flex items-center justify-between py-2">
        <NavigationMenu className="justify-start">
          <NavigationMenuList className="gap-1">
            {/* Popular Test/Packages */}
            <NavSubmenu
              triggerLabel="Popular Test/Packages"
              width="w-[400px]"
              sections={[
                {
                  title: 'Popular Tests',
                  items: packageState?.mostPopularTest?.length
                    ? packageState.mostPopularTest
                    : sharedCatalog?.mostPopularTest || [],
                  getItemId: (item) => item?.productDto?.id,
                  getItemName: (item) => item?.productDto?.name,
                  getItemUrl: (item) =>
                    isNonCity
                      ? getNonCityProductUrl(item?.productDto)
                      : getProductUrl(
                          item?.productDto,
                          cityState?.currentCityObj?.slugName
                        ),
                  limit: 5,
                },
                {
                  title: 'Popular Packages',
                  items: packageState?.mostPopularPackage?.length
                    ? packageState.mostPopularPackage
                    : sharedCatalog?.mostPopularPackage || [],
                  getItemId: (item) => item?.productDto?.id,
                  getItemName: (item) => item?.productDto?.name,
                  getItemUrl: (item) =>
                    isNonCity
                      ? getNonCityProductUrl(item?.productDto)
                      : getProductUrl(
                          item?.productDto,
                          cityState?.currentCityObj?.slugName
                        ),
                  limit: 5,
                },
              ]}
              viewAllUrl={getPopularHealthCheckupUrl(
                cityState?.currentCityObj?.slugName
              )}
              viewAllText="View All →"
            />

            {/* Full Body Checkup */}
            <NavSubmenu
              triggerLabel="Full Body Checkup"
              sections={[
                {
                  title: 'Health Packages',
                  items: packageState?.carePackageList?.length
                    ? packageState.carePackageList
                    : sharedCatalog?.carePackageList || [],
                  getItemId: (item) => item?.packageId,
                  getItemName: (item) => item?.name,
                  getItemUrl: (item) =>
                    isNonCity
                      ? getCarePackageNonCityCategoryUrl(item?.name || '')
                      : getCarePackageCategoryUrl(
                          linkCitySlug,
                          item?.name || ''
                        ),
                  limit: 8,
                },
              ]}
            />

            {/* Disease Categories */}
            <NavSubmenu
              triggerLabel="Disease Categories"
              triggerHref={getDiseaseCategoriesUrl(linkCitySlug)}
              sections={[
                {
                  title: 'Test by Disease',
                  items: packageState?.diseaseCategoryDtoList?.length
                    ? packageState.diseaseCategoryDtoList
                    : sharedCatalog?.diseaseCategoryDtoList || [],
                  getItemId: (item) => item?.diseaseCategory,
                  getItemName: (item) => item?.diseaseCategoryName,
                  getItemUrl: (item) =>
                    getDiseaseCategoriesProductListUrl(
                      linkCitySlug,
                      item?.diseaseCategoryName
                    ),
                  limit: 8,
                },
              ]}
              viewAllUrl={getDiseaseCategoriesUrl(linkCitySlug)}
              viewAllText="View All Categories →"
            />

            {/* My Reports */}
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href={MY_REPORTS_PAGE}
                  onClick={myReportClickHandler}
                  className={cn(
                    navigationMenuTriggerStyle(),
                    'text-label-md-medium leading-6 text-gray-700 !bg-transparent hover:!text-primary-600 cursor-pointer transition-colors !rounded-none !border-b-0 nav-tab-indicator'
                  )}
                >
                  My Reports
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            {/* Request a callback */}
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <button
                  onClick={() =>
                    dispatch(
                      updateCommonReducerData({
                        ...commonState,
                        requestCallbackModalIsOpen: {
                          modalIsOpen: true,
                          type: FORM_TYPE_LAB_TEST,
                        },
                      })
                    )
                  }
                  className={cn(
                    navigationMenuTriggerStyle(),
                    'text-label-md-medium leading-6 text-gray-700 !bg-transparent hover:!text-primary-600 cursor-pointer transition-colors !rounded-none !border-b-0 nav-tab-indicator'
                  )}
                >
                  Request Callback
                </button>
              </NavigationMenuLink>
            </NavigationMenuItem>

            {/* Nearby Labs */}
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href={NEARBY_LABS_PAGE}
                  onClick={() =>
                    trackEvent(eventConstants.NEARBY_LABS, {
                      u_mb: loggedInMobileNumber || '',
                    })
                  }
                  className={cn(
                    navigationMenuTriggerStyle(),
                    'text-label-md-medium leading-6 text-gray-700 !bg-transparent hover:!text-primary-600 cursor-pointer transition-colors !rounded-none !border-b-0 nav-tab-indicator'
                  )}
                >
                  Nearby Labs
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            {/* <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href={BLOG_PAGE}
                  className={cn(
                    navigationMenuTriggerStyle(),
                    'text-label-md-medium leading-6 text-gray-700 !bg-transparent hover:!text-primary-600 cursor-pointer transition-colors !rounded-none !border-b-0 nav-tab-indicator'
                  )}
                >
                  Blogs
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem> */}
          </NavigationMenuList>
        </NavigationMenu>

        <div className="flex items-center gap-2">
          <span className="text-label-sm-medium text-gray-600">Get app:</span>
          <a
            href={SUBSIDIARY_CONFIG?.PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center"
          >
            <PlayStoreIcon />
          </a>
          <a
            href={SUBSIDIARY_CONFIG?.APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center"
          >
            <AppStoreIcon />
          </a>
        </div>
      </div>
    </div>
  )
}

export default SecondaryNav
